import React, { useState } from 'react';
import Card from '../ui/Card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { DashboardFilters } from '../../pages/Dashboard';
const baseCountData = [{
  time: '00:00',
  current: 1200,
  previous: 900
}, {
  time: '04:00',
  current: 800,
  previous: 750
}, {
  time: '08:00',
  current: 3500,
  previous: 3100
}, {
  time: '12:00',
  current: 8900,
  previous: 7800
}, {
  time: '16:00',
  current: 7200,
  previous: 6900
}, {
  time: '20:00',
  current: 9500,
  previous: 8200
}, {
  time: '23:59',
  current: 4100,
  previous: 3800
}];
const baseValueData = [{
  time: '00:00',
  current: 15,
  previous: 12
}, {
  time: '04:00',
  current: 8,
  previous: 7
}, {
  time: '08:00',
  current: 42,
  previous: 38
}, {
  time: '12:00',
  current: 115,
  previous: 95
}, {
  time: '16:00',
  current: 85,
  previous: 82
}, {
  time: '20:00',
  current: 130,
  previous: 110
}, {
  time: '23:59',
  current: 55,
  previous: 48
}];
const timeMultipliers = {
  Today: 1,
  Week: 7,
  MTD: 22,
  YTD: 265
};
const schemeMultipliers: Record<string, number> = {
  'All Schemes': 1,
  Visa: 0.35,
  MasterCard: 0.28,
  PayPak: 0.2,
  UnionPay: 0.1,
  RAAST: 0.07
};
const channelMultipliers: Record<string, number> = {
  'All Channels': 1,
  POS: 0.45,
  'E-commerce': 0.35,
  'RAAST QR': 0.2
};
interface TransactionTrendsProps {
  filters: DashboardFilters;
}
export const TransactionTrends: React.FC<TransactionTrendsProps> = ({
  filters
}) => {
  const [metric, setMetric] = useState<'count' | 'value'>('count');
  const {
    timeRange,
    scheme,
    channel
  } = filters;
  const timeMult = timeMultipliers[timeRange];
  const schemeMult = schemeMultipliers[scheme];
  const channelMult = channelMultipliers[channel];
  const totalMult = timeMult * schemeMult * channelMult;
  const baseData = metric === 'count' ? baseCountData : baseValueData;
  const chartData = baseData.map(d => ({
    time: d.time,
    current: Math.round(d.current * totalMult),
    previous: Math.round(d.previous * totalMult)
  }));
  const formatYAxis = (val: number) => {
    if (metric === 'count') return new Intl.NumberFormat('en', {
      notation: 'compact'
    }).format(val);
    return `${val}M`;
  };
  const formatTooltip = (val: number) => {
    if (metric === 'count') return [new Intl.NumberFormat('en').format(val), 'Transactions'];
    return [`PKR ${val}M`, 'Value'];
  };
  return <Card data-id="element-331">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8" data-id="element-332">
        <div data-id="element-333">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight" data-id="element-334">
            Transaction Trends
          </h3>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1" data-id="element-335">
            Current period vs Previous period
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl shadow-inner" data-id="element-336">
          <button onClick={() => setMetric('count')} className={`px-5 py-2 text-xs font-bold rounded-lg transition-all ${metric === 'count' ? 'bg-white dark:bg-slate-700 text-accent-600 dark:text-accent-400 shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-600' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`} data-id="element-337">
            Volume
          </button>
          <button onClick={() => setMetric('value')} className={`px-5 py-2 text-xs font-bold rounded-lg transition-all ${metric === 'value' ? 'bg-white dark:bg-slate-700 text-accent-600 dark:text-accent-400 shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-600' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`} data-id="element-338">
            Value
          </button>
        </div>
      </div>

      <div className="h-[340px] w-full" data-id="element-339">
        <ResponsiveContainer width="100%" height="100%" data-id="element-340">
          <AreaChart data={chartData} margin={{
          top: 10,
          right: 10,
          left: 0,
          bottom: 0
        }} data-id="element-341">
            <defs data-id="element-342">
              <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1" data-id="element-343">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} data-id="element-344" />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} data-id="element-345" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:stroke-slate-800" data-id="element-346" />
            <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
            fontSize: 12,
            fill: '#64748b',
            fontWeight: 500
          }} dy={15} data-id="element-347" />
            <YAxis axisLine={false} tickLine={false} tick={{
            fontSize: 12,
            fill: '#64748b',
            fontWeight: 500
          }} tickFormatter={formatYAxis} data-id="element-348" />
            <Tooltip formatter={formatTooltip} contentStyle={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
            fontWeight: 600,
            padding: '12px 16px'
          }} data-id="element-349" />
            <Legend verticalAlign="top" height={40} iconType="circle" wrapperStyle={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#64748b'
          }} data-id="element-350" />
            <Area type="monotone" dataKey="previous" name="Previous Period" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="5 5" fill="none" data-id="element-351" />
            <Area type="monotone" dataKey="current" name="Current Period" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorCurrent)" activeDot={{
            r: 6,
            strokeWidth: 0,
            fill: '#4f46e5'
          }} data-id="element-352" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>;
};